import ContentItem from '../api/content/ContentItem';
import attachVideoElement from '../common/Reporter/attachVideoElement';
import logger from '../log';
import setupPlaybackStarted from '../util/setupPlaybackStarted';
import { DeliveryMode, SourceType } from '../common/constants';
import { getSourceTypeForMime } from '../common/utils/sourceTypeUtils';
import {
  InterceptBinder,
  BoolFunction,
  BindOptionsFunction
} from './utils/InterceptBinder';
import EventEmitter from '../common/EventEmitter';
import { InternalEvents, SourceSetEventOptions } from '../events';
import { dispatchInfo } from '../events/dispatch';
import { handleMonitoringHlsManifest } from '../interceptor/maybeModifyManifest';
import { TechApi } from '../bootloader/tech';
import PlayerElement, {
  playerElementAsMaybeMediaElement
} from '../util/PlayerElement';

const log = logger.getLogger(__filename);

const { ECDN11, WEBRTC } = DeliveryMode;
export type CleanupMethod = () => void;

export interface ExtendedSourceSetEventOptions<T>
  extends SourceSetEventOptions<T> {
  masterPlaylist?: string;
  masterPlaylistUrl?: string;
}

export default abstract class BaseInteraction<T> {
  allowedModes: DeliveryMode[];
  binder: InterceptBinder;
  cleanupMethods: CleanupMethod[];
  element?: HTMLVideoElement;
  player: T;
  tech: TechApi;
  _latestElement?: HTMLMediaElement;

  constructor(player: T, tech: TechApi, allowedModes?: DeliveryMode[]) {
    this.player = player;
    if (typeof player === 'undefined') {
      log.error('No player passed in no way to initialize the ksdk');
    }
    this._latestElement = playerElementAsMaybeMediaElement(player);
    this.tech = tech;
    this.allowedModes = allowedModes || [WEBRTC, ECDN11];
    this.binder = new InterceptBinder();
    this.cleanupMethods = [];
  }

  // Due to compilation, any functions that are called or set to be called in
  // the constructor could end up interacting with an incomplete player
  // interaction. In order to counteract that we've added some check in the
  // mediaElement function, and moved setting up callbacks to this object
  // after the constructor is done.
  initialize(): void {
    const emitter = EventEmitter.getInstance();
    emitter.addEventListener(InternalEvents.SOURCE_SET, event => {
      this.reinitialize(event.data);
    });
    setupPlaybackStarted(this);
  }

  // To enable for a sub-class, the `resetEventsAndInterceptors` function needs
  // to be implemented with code that resets the player `resetMediaElement` and
  // resets all interceptors, and any additional event listeners.
  reinitialize(opts: ExtendedSourceSetEventOptions<T>): void {
    if (opts.masterPlaylistUrl && opts.masterPlaylist) {
      handleMonitoringHlsManifest(opts.masterPlaylistUrl, opts.masterPlaylist);
    }
    log.trace('reinitialize called with:', opts);
    this.resetEventsAndInterceptors(opts);
    opts.resolve();
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  resetEventsAndInterceptors(opts: SourceSetEventOptions<T>): void {}

  /**
   * This is for players which are also the mediaElement.
   */
  protected resetMediaElement(ele?: T | null): void {
    if (ele === null || (ele && ele !== this.player)) {
      this.cleanupMethods.forEach(method => method());
      this.cleanupMethods = [];
      this.player = ele as T;
      if (ele) {
        this.addElementListeners();
      }
    }
  }

  /**
   * Binds an interceptor to the player.
   * @param shouldIntercept - a function that returns true if kollective intercept should happen
   * @param getOptions - a function that returns the options necessary for KollectiveXHR
   */
  bindInterceptor(
    shouldIntercept: BoolFunction,
    getOptions: BindOptionsFunction,
    resetIsControlled: BoolFunction
  ): void {
    this.binder.updateBinder(shouldIntercept, getOptions, resetIsControlled);
  }

  // Clear source for SRV-712.
  abstract clearSource(): void;
  /**
   * Internal error message generator, for unfinished and/or not yet implemented features.
   */
  generatePlannedErrorMessage(method: string, integration: string): string {
    return `${method} for ${integration} has yet to be implemented, but is planned. Feel free to reach out to Kollective regarding this issue.`;
  }

  /**
   * 10 agent playback specific. This communicates to the 10 agent that it needs to switch to cdn playback.
   * @param items - list of content items
   */
  switchToCDN(items: ContentItem[]): ContentItem[] {
    return this.loader(items);
  }

  /**
   *
   * @param items - content items to set on player
   */
  fallbackToCDN(items: ContentItem[]): ContentItem[] {
    return this.loader(items);
  }

  /**
   *
   * @param items - content items to set on player
   */
  playKollective(items: ContentItem[]): ContentItem[] | undefined {
    const filtered = this.getAllowedSources(items);
    if (filtered.length > 0) {
      return this.loader(filtered, true);
    } else {
      throw Error('No available allowed sources');
    }
  }

  /**
   * Adds Player Event Listeners
   */
  addEventListeners(): void {
    this.addElementListeners();
  }

  private addElementListeners(): void {
    const element = this.mediaElement();
    if (element) {
      this.cleanupMethods.push(
        attachVideoElement(element as PlayerElement, this.tech, this)
      );
    } else {
      const msg = 'No mediaElement found, unable to attachVideoElement';
      dispatchInfo(`Warning: ${msg}`);
      log.warn(msg, element);
    }
  }

  getPlayer(): T {
    return this.player;
  }

  setVideoElement(element: HTMLVideoElement): void {
    this.element = element;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  abstract loader(items: ContentItem[], isFiltered?: boolean): ContentItem[];

  abstract mediaElement(): HTMLMediaElement | undefined;

  /**
   * @returns an array of allowed source types for the implementing interaction class.
   */
  abstract getAllowedSourceTypes(): SourceType[];

  getAllowedSources(items: ContentItem[]): ContentItem[] {
    return items.filter(i => {
      const sourceType = getSourceTypeForMime(i.data.mime, i.url);
      return this.getAllowedSourceTypes().includes(sourceType as SourceType);
    });
  }

  /**
   * Get the HTMLElement or wrapper playing the video/audio.
   * Looks at as many places at possible to find the best object.
   */
  latestElement(e?: unknown, p?: unknown): HTMLMediaElement | undefined {
    this._latestElement =
      playerElementAsMaybeMediaElement(e) ??
      playerElementAsMaybeMediaElement(p) ??
      playerElementAsMaybeMediaElement(this.mediaElement()) ??
      (this._latestElement as HTMLMediaElement);
    return this._latestElement;
  }

  cleanup(): void {
    this.resetMediaElement(null);
    this.binder.reset();
  }
}
